<script lang="ts">
  /**
   * This component can be used to create Popovers with. Usage:
   *
   * import AUPopover from 'component_library/components/svelte/ui/AUPopover.svelte';
   * let popover: AUPopover
   *
   * <button on:click={popover.open}>Show</button>
   *
   * <AUPopover bind:this={popover}>
   *   <p>My interesting content</p>
   * </AUPopover>
   */
  import { fly } from "svelte/transition"
  import { writable } from "svelte/store"
  import { offset, flip, shift } from "svelte-floating-ui/dom"
  import { createFloatingActions, arrow } from "svelte-floating-ui"
  import { nanoid } from "nanoid"

  export let startOpened: boolean = false
  export let fitToContent: boolean = false
  export let placement: "top-start" | "top" | "top-end"
  export let showArrow: boolean = true
  let expanded = writable(startOpened)

  const popoverId = `popover-${nanoid(6)}`

  export const open = () => {
    expanded.set(true)
  }

  export const close = () => {
    expanded.set(false)
  }

  const arrowRef = writable<HTMLDivElement | null>(null)
  const [_floatingRef, floatingContent] = createFloatingActions({
    strategy: "fixed",
    placement,
    middleware: [
      offset(showArrow ? 16 : undefined),
      flip(),
      shift(),
      showArrow ? arrow({ element: arrowRef }) : null,
    ],
    autoUpdate: true,
    onComputed({ placement, middlewareData }) {
      if (!middlewareData.arrow) return

      const { x, y } = middlewareData.arrow
      const placementRoot = (placement.split("-")[0] || "dummyProp") as
        | "top"
        | "right"
        | "bottom"
        | "left"
        | "dummyProp"
      const staticSide = {
        top: "bottom",
        right: "left",
        bottom: "top",
        left: "right",
        dummyProp: "dummyProp",
      }[placementRoot]

      Object.assign($arrowRef?.style || {}, {
        left: x != null ? `${x}px` : "",
        top: y != null ? `${y}px` : "",
        [staticSide]: "calc(-1 * var(--au-spacing-xs))",
      })
    },
  })

  export const floatingRef = _floatingRef
  export const triggerRef = (node: HTMLElement) => {
    node.tabIndex = 0
    node.setAttribute("aria-haspopup", "true")
    node.setAttribute("aria-controls", popoverId)
    expanded.subscribe((value) => {
      node.setAttribute("aria-expanded", String(value))
    })
  }

  const widthStyle = fitToContent ? "width: max-content;" : "width: 100vw;"
</script>

{#if $expanded}
  <div
    id={popoverId}
    class="au-popover"
    style={widthStyle}
    use:floatingContent
    transition:fly={{ y: 50, duration: 200 }}
  >
    <div class="au-popover__content">
      <slot />
    </div>
    {#if showArrow}
      <div class="au-popover__arrow" bind:this={$arrowRef} />
    {/if}
  </div>
{/if}

<style>
  .au-popover {
    position: fixed;
    z-index: var(--au-z-index-widget);
    max-width: 320px;
    border-radius: var(--au-border-radius-default);
    border: 1px solid var(--au-line-color-neutral);
    box-shadow: var(--au-box-shadow-strong);
  }

  .au-popover__content {
    padding: var(--au-spacing-md);
    border-radius: var(--au-border-radius-default);
    background-color: var(--au-background-default);
  }

  .au-popover__arrow {
    position: absolute;
    width: var(--au-spacing-md);
    height: var(--au-spacing-md);
    transform: rotate(-45deg);
    background: var(--au-background-default);
    border: 1px solid var(--au-line-color-neutral);
    box-shadow: var(--au-box-shadow-strong);
    z-index: -1;
  }
</style>
